import { createContext, useContext, useEffect, useRef, useState } from "react";
import { WS_URL, api } from "@/lib/api";

const RateCtx = createContext(null);

export function RateProvider({ children }) {
  const [rates, setRates] = useState({});
  const [karats, setKarats] = useState({});
  const [lastFlash, setLastFlash] = useState({});
  const wsRef = useRef(null);
  const prevRef = useRef({});

  useEffect(() => {
    api.get("/rates").then((r) => {
      setRates(r.data);
      prevRef.current = r.data;
    }).catch(() => {
      // 402 / no subscription — silently ignore, ticker will stay empty
    });

    // Fetch Harem coin/karat prices (Çeyrek, Yarım, Tam, Ata, Cumhuriyet) directly.
    const loadKarats = () => {
      api.get("/karats").then((r) => {
        const k = r.data?.karats || {};
        setKarats(k);
      }).catch(() => {});
    };
    loadKarats();
    // Poll every 15 seconds — Harem WS covers grams; coins update slightly less frequently.
    const karatInterval = setInterval(loadKarats, 15000);

    let stopped = false;
    const connect = () => {
      if (stopped) return;
      try {
        const ws = new WebSocket(WS_URL());
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "snapshot") {
              setRates(msg.data || {});
              prevRef.current = msg.data || {};
              // Snapshot may include karats too
              if (msg.karats) setKarats(msg.karats);
            } else if (msg.type === "rateUpdated") {
              const d = msg.data;
              setRates((prev) => {
                const prevRate = prev[d.metal_type]?.selling_price;
                const dir = prevRate == null ? null : d.selling_price > prevRate ? "up" : d.selling_price < prevRate ? "down" : null;
                if (dir) {
                  setLastFlash((f) => ({ ...f, [d.metal_type]: { dir, at: Date.now() } }));
                }
                return { ...prev, [d.metal_type]: d };
              });
              // Refresh karats when gram rates change (they move together)
              loadKarats();
            } else if (msg.type === "karatsUpdated") {
              setKarats(msg.data || {});
            }
          } catch (e) {}
        };
        ws.onclose = () => { setTimeout(connect, 3000); };
        ws.onerror = () => { try { ws.close(); } catch(e){} };
      } catch (e) {
        setTimeout(connect, 3000);
      }
    };
    connect();
    return () => { stopped = true; clearInterval(karatInterval); try { wsRef.current?.close(); } catch(e){} };
  }, []);

  return (
    <RateCtx.Provider value={{ rates, karats, lastFlash }}>
      {children}
    </RateCtx.Provider>
  );
}

export const useRates = () => useContext(RateCtx);
